/**
 * Bild-Zuschnitt: Rechteck mit vier einzeln ziehbaren Ecken. Schreibt das
 * Ergebnis dorthin zurück, wo das Original herkam (gleicher Dateiname) –
 * jede Stelle, die darauf verweist (Markdown-Vorschau über `pb:<id>/<datei>`,
 * das zusammengeführte Arbeits-PDF beim nächsten Öffnen), zeigt danach
 * automatisch den zugeschnittenen Stand, ohne selbst etwas wissen zu müssen.
 *
 * Nicht-destruktiv: vor dem ALLERERSTEN Zuschnitt eines Bildes wird das
 * unveränderte Original lokal gesichert (IndexedDB, siehe offline-store.ts).
 * Jeder weitere Zuschnitt – auch nach einem Reload – geht von diesem
 * Original aus, nie vom bereits zugeschnittenen Stand: die Auswahl lässt
 * sich also jederzeit wieder vergrößern statt sich mit jedem Zuschnitt
 * weiter zu verkleinern. Ein eigener Knopf setzt das Bild direkt komplett
 * zurück.
 */
import { h } from "@/lib/dom";
import { button, openModal, toast } from "@/components/ui";
import { pbErrorMessage } from "@/services";
import { offlineStore } from "@/services/offline/offline-store";

type Corner = "tl" | "tr" | "bl" | "br";

export interface CropDialogOptions {
  imageUrl: string;
  /** "image/png" behält Transparenz, sonst wird als JPEG gespeichert. */
  mimeType?: string;
  /** Eindeutiger Schlüssel (z. B. Material-ID) für die lokale Original-Sicherung. */
  backupKey: string;
  /** Bekommt den zugeschnittenen (oder bei "Zurücksetzen" den vollen) Blob. */
  onSave: (blob: Blob) => Promise<void>;
}

const backupKeyFor = (key: string) => `cropOriginal:${key}`;

export async function openCropDialog(opts: CropDialogOptions): Promise<void> {
  // Erster Zuschnitt dieses Bildes: das aktuelle (noch unveränderte) Bild als
  // Original sichern, BEVOR irgendwas passiert. Jeder spätere Aufruf nutzt
  // stattdessen dieses gesicherte Original als Quelle.
  let original = await offlineStore.get<Blob>(backupKeyFor(opts.backupKey));
  if (!original) {
    try {
      original = await fetch(opts.imageUrl).then((r) => r.blob());
      await offlineStore.set(backupKeyFor(opts.backupKey), original);
    } catch {
      /* Sicherung fehlgeschlagen (z. B. offline) – Zuschnitt geht trotzdem, nur ohne Zurücksetzen-Option */
      original = undefined;
    }
  }
  const sourceUrl = original ? URL.createObjectURL(original) : opts.imageUrl;

  const img = h("img", { src: sourceUrl, class: "crop-img", crossorigin: "anonymous" }) as HTMLImageElement;
  const stage = h("div", { class: "crop-stage" }, img);
  const rectEl = h("div", { class: "crop-rect" });
  const handles: Record<Corner, HTMLElement> = {
    tl: h("div", { class: "crop-handle crop-handle-tl" }),
    tr: h("div", { class: "crop-handle crop-handle-tr" }),
    bl: h("div", { class: "crop-handle crop-handle-bl" }),
    br: h("div", { class: "crop-handle crop-handle-br" }),
  };
  stage.append(rectEl, ...Object.values(handles));

  // Zuschnitt-Rechteck als Anteile (0..1) der Bildfläche – unabhängig von der
  // tatsächlichen Anzeigegröße, gilt am Ende genauso für die volle Auflösung.
  let x1 = 0.05,
    y1 = 0.05,
    x2 = 0.95,
    y2 = 0.95;

  function layout(): void {
    const w = stage.clientWidth;
    const h_ = stage.clientHeight;
    rectEl.style.left = `${x1 * w}px`;
    rectEl.style.top = `${y1 * h_}px`;
    rectEl.style.width = `${(x2 - x1) * w}px`;
    rectEl.style.height = `${(y2 - y1) * h_}px`;
    const at = (fx: number, fy: number) => ({ left: `${fx * w}px`, top: `${fy * h_}px` });
    Object.assign(handles.tl.style, at(x1, y1));
    Object.assign(handles.tr.style, at(x2, y1));
    Object.assign(handles.bl.style, at(x1, y2));
    Object.assign(handles.br.style, at(x2, y2));
  }

  function dragCorner(corner: Corner, e: PointerEvent): void {
    e.preventDefault();
    const move = (ev: PointerEvent) => {
      const rect = stage.getBoundingClientRect();
      const fx = Math.min(1, Math.max(0, (ev.clientX - rect.left) / rect.width));
      const fy = Math.min(1, Math.max(0, (ev.clientY - rect.top) / rect.height));
      const MIN = 0.05;
      if (corner === "tl") {
        x1 = Math.min(fx, x2 - MIN);
        y1 = Math.min(fy, y2 - MIN);
      } else if (corner === "tr") {
        x2 = Math.max(fx, x1 + MIN);
        y1 = Math.min(fy, y2 - MIN);
      } else if (corner === "bl") {
        x1 = Math.min(fx, x2 - MIN);
        y2 = Math.max(fy, y1 + MIN);
      } else {
        x2 = Math.max(fx, x1 + MIN);
        y2 = Math.max(fy, y1 + MIN);
      }
      layout();
    };
    const up = () => {
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", up);
    };
    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", up);
  }
  for (const corner of Object.keys(handles) as Corner[]) {
    handles[corner].addEventListener("pointerdown", (e) => dragCorner(corner, e));
  }

  // `img.complete` ist bei einem bereits geladenen (z. B. gecachten) Bild schon
  // vor dem Anhängen des Listeners `true` – dann feuert "load" nie wieder, und
  // Rechteck/Ecken blieben ohne Layout (unsichtbar/falsch positioniert).
  if (img.complete && img.naturalWidth) layout();
  img.addEventListener("load", layout);
  window.addEventListener("resize", layout);

  const applyBtn = button({ label: "Zuschneiden", variant: "primary", onClick: () => void apply() });
  const resetBtn = original
    ? button({
        label: "Zurücksetzen",
        variant: "ghost",
        onClick: () => void reset(),
      })
    : null;

  async function apply(): Promise<void> {
    applyBtn.disabled = true;
    try {
      const nw = img.naturalWidth;
      const nh = img.naturalHeight;
      const canvas = document.createElement("canvas");
      canvas.width = Math.round((x2 - x1) * nw);
      canvas.height = Math.round((y2 - y1) * nh);
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Zuschnitt nicht möglich (Canvas).");
      ctx.drawImage(img, x1 * nw, y1 * nh, canvas.width, canvas.height, 0, 0, canvas.width, canvas.height);
      const mime = opts.mimeType === "image/png" ? "image/png" : "image/jpeg";
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, mime, 0.92));
      if (!blob) throw new Error("Zuschnitt nicht möglich.");
      await opts.onSave(blob);
      toast("Bild zugeschnitten.", "success");
      handle.close();
    } catch (err) {
      toast(pbErrorMessage(err), "error");
    } finally {
      applyBtn.disabled = false;
    }
  }

  async function reset(): Promise<void> {
    if (!original) return;
    if (resetBtn) resetBtn.disabled = true;
    try {
      await opts.onSave(original);
      toast("Ausgangsbild wiederhergestellt.", "success");
      handle.close();
    } catch (err) {
      toast(pbErrorMessage(err), "error");
    } finally {
      if (resetBtn) resetBtn.disabled = false;
    }
  }

  const handle = openModal({
    title: "Bild zuschneiden",
    wide: true,
    guardUnsaved: false,
    onClose: () => {
      if (original) URL.revokeObjectURL(sourceUrl);
    },
    body: h(
      "div",
      { class: "col" },
      h("p", { class: "muted text-sm" }, "Ecken ziehen, um den Ausschnitt anzupassen. Das Original bleibt erhalten – jederzeit wieder vergrößerbar."),
      stage
    ),
    footer: h(
      "div",
      { class: "row" },
      button({ label: "Abbrechen", variant: "ghost", onClick: () => handle.close() }),
      ...(resetBtn ? [resetBtn] : []),
      applyBtn
    ),
  });
}
